import { DataTableClient } from '@/components/data-table';
import PageWithBreadcrumbs from '@/components/page-with-breadcrumbs';
import { Button } from '@/components/ui/button';
import { fetchClient } from '@/lib/api';
import { useTranslation } from '@/lib/i18n';
import { permissaoGuard } from '@/lib/permissions';
import { buildHttpLink } from '@/lib/url';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import Download from '~icons/material-symbols/download';

interface PacoteRow {
  grupo: string;
  descricao: string;
  caminho: string;
}

export const Route = createFileRoute(
  '/__authenticated/suporte/pacote-comunicacao/',
)({
  beforeLoad: async () => {
    await permissaoGuard('PacoteComunicacao');
  },
  loader: async () => {
    const { data } = await fetchClient.GET('/suporte/pacotes-comunicacao');
    return data ?? [];
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('private');
  const pacotes = Route.useLoaderData();

  const linhas: PacoteRow[] = pacotes.map(item => ({
    grupo: item.grupo ?? '',
    descricao: item.descricao ?? '',
    caminho: item.caminho ?? '',
  }));

  const columns: ColumnDef<PacoteRow>[] = [
    {
      accessorKey: 'descricao',
      header: t($ => $.suporte.pacoteComunicacao.descricao),
      cell: ({ row }) => row.original.descricao,
    },
    {
      id: 'acao',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button asChild size="sm">
            <a
              href={buildHttpLink(row.original.caminho)}
              target="_blank"
              rel="noreferrer"
            >
              <Download />
              {t($ => $.suporte.pacoteComunicacao.baixar)}
            </a>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageWithBreadcrumbs
      breadcrumbs={[
        t($ => $.navbar.suporte),
        t($ => $.navbar.pacoteComunicacao),
      ]}
    >
      <div>
        <h1 className="text-2xl font-semibold">
          {t($ => $.suporte.pacoteComunicacao.titulo)}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t($ => $.suporte.pacoteComunicacao.subtitulo)}
        </p>
      </div>

      <DataTableClient
        columns={columns}
        data={linhas}
        getRowGroup={row => row.grupo}
        className="[&_td]:border-r-0 [&_td:not([colspan])]:text-muted-foreground [&_th]:border-r-0"
        emptyState={t($ => $.suporte.pacoteComunicacao.semResultados)}
      />
    </PageWithBreadcrumbs>
  );
}
